/**
 * Serviço centralizado para operações de sessão do CorelDRAW
 * @module SessionService
 * 
 * ✅ ATUALIZADO: Todas as actions usam sendActionGuarded (HMAC obrigatório)
 * session:hello é TOKEN_EXEMPT mas ainda exige HMAC
 */

import { corelCommunication } from '../communication';
import { debugLog, sendToTerminal } from '../../terminalLogger';
import { actionDebugger } from './actionDebugger';
import { LogCategory } from '@/config/logging';
import type {
  SessionHandshakeOptions,
  SessionHandshakeResult,
  SessionHelloPayload
} from './session.types';

/**
 * Flag global para evitar múltiplos handshakes simultâneos
 */
let inFlightHandshake: Promise<SessionHandshakeResult> | null = null;

/**
 * Serviço centralizado para gerenciamento de sessão
 */
export class SessionService {
  /**
   * Envia session:hello para o host C#
   * 
   * Fire-and-forget - não aguarda resposta explícita
   * O token será recebido via mensagem assíncrona do host
   * 
   * @param payload Payload opcional
   */
  static sendHello(payload?: SessionHelloPayload): void {
    try {
      // Incluir dockerId se disponível (recomendado conforme documentação)
      // dockerId sempre está disponível (com fallback automático do backend)
      const data = {
        ...(payload || { timestamp: Date.now() }),
        ...(window.dockerId && { dockerId: window.dockerId })
      };
      corelCommunication.sendActionGuarded('session:hello', data);
      
      sendToTerminal('🔵 [SESSION] sendHello enviado', {
        timestamp: data.timestamp,
        dockerId: window.dockerId || 'not-provided'
      }, LogCategory.CONNECTION);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      sendToTerminal('❌ [SESSION] Erro em sendHello', { error: errorMessage }, LogCategory.ERROR);
    }
  }

  /**
   * Garante que o session token está disponível
   * 
   * Se já existir token, retorna imediatamente
   * Caso contrário, envia session:hello e aguarda token via polling
   * 
   * ⚠️ SINGLETON: Múltiplas chamadas simultâneas compartilham a mesma Promise
   * 
   * @param options Opções de configuração
   * @returns Promise com resultado do handshake
   */
  static async ensureToken(
    options?: SessionHandshakeOptions
  ): Promise<SessionHandshakeResult> {
    // Se já tem token, retorna imediatamente
    try {
      const existingToken = (window as any)?.sessionToken;
      if (existingToken) {
        sendToTerminal('✅ [SESSION] Token já disponível', { immediate: true }, LogCategory.CONNECTION);
        return {
          success: true,
          token: existingToken,
          elapsedMs: 0,
          timedOut: false
        };
      }
    } catch {}

    // Se já existe handshake em andamento, reutiliza
    if (inFlightHandshake) {
      sendToTerminal('ℹ️ [SESSION] Reutilizando handshake em andamento', {}, LogCategory.CONNECTION);
      return inFlightHandshake;
    }

    // Inicia novo handshake
    const timeoutMs = options?.timeoutMs ?? 3000;
    const pollingIntervalMs = options?.pollingIntervalMs ?? 100;
    const sendHello = options?.sendHello ?? true;

    sendToTerminal('🔐 [SESSION] Iniciando handshake', { timeoutMs }, LogCategory.CONNECTION);

    inFlightHandshake = new Promise<SessionHandshakeResult>((resolve) => {
      // Envia session:hello se solicitado
      if (sendHello) {
        try {
          this.sendHello();
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          sendToTerminal('❌ [SESSION] Erro ao enviar sendHello', { error: errorMessage }, LogCategory.ERROR);
        }
      }

      const startedAt = Date.now();
      
      const interval = window.setInterval(() => {
        const elapsedMs = Date.now() - startedAt;
        const timedOut = elapsedMs >= timeoutMs;
        
        // Verificar se token está disponível
        let hasToken = false;
        let token: string | undefined;
        
        try {
          token = (window as any)?.sessionToken;
          hasToken = !!token;
        } catch {}

        if (hasToken || timedOut) {
          // Limpar interval e flag
          try {
            window.clearInterval(interval);
          } catch {}
          inFlightHandshake = null;

          const result: SessionHandshakeResult = {
            success: hasToken,
            token,
            elapsedMs,
            timedOut
          };

          if (hasToken) {
            sendToTerminal('✅ [SESSION] Token obtido', { elapsedMs, tokenPreview: token ? `${token.substring(0, 8)}...` : undefined }, LogCategory.CONNECTION);
          } else {
            sendToTerminal('⏱️ [SESSION] Timeout ao aguardar token', { elapsedMs, timeoutMs }, LogCategory.ERROR);
          }

          // Log já foi feito acima

          resolve(result);
        }
      }, pollingIntervalMs);
    });

    return inFlightHandshake;
  }

  /**
   * Obtém o token de sessão atual (se existir)
   * 
   * @returns Token de sessão ou null
   */
  static getCurrentToken(): string | null {
    try {
      return (window as any)?.sessionToken || null;
    } catch {
      return null;
    }
  }

  /**
   * Verifica se existe token de sessão
   * 
   * @returns true se token existe
   */
  static hasToken(): boolean {
    return !!this.getCurrentToken();
  }

  /**
   * Limpa a flag de handshake em andamento
   * 
   * ⚠️ USO INTERNO - Apenas para casos excepcionais
   */
  static _clearInFlightFlag(): void {
    inFlightHandshake = null;
  }
}

/**
 * Função auxiliar para compatibilidade com código existente
 * @deprecated Use SessionService.ensureToken() diretamente
 */
export async function ensureSessionHelloOnce(timeoutMs: number = 3000): Promise<boolean> {
  const result = await SessionService.ensureToken({ timeoutMs });
  return result.success;
}

